import { Jurisdiction } from '@enview/interface/types/Jurisdiction';
import { OrganizationUser } from '@enview/interface/types/OrganizationUser';
import { MultiSelectOption } from '../scenes/Shared/Forms/MultiSelectForm';
import { Organization } from '@enview/interface/types/Organization';

export const getJurisdictionAccess = (
  legislatureOptions: MultiSelectOption<string>[],
  supportedJurisdictions: Jurisdiction[],
): MultiSelectOption<string>[] => {
  return legislatureOptions.filter((leg) =>
    supportedJurisdictions.find((jur) => jur.abbreviation === leg.value),
  );
};

/*
  Implements filter-by-selected-team logic used across a few different components
  (bill search, nationwide hearings search, etc). supportedJurisdictions is probably
  the output of getUserSupportedJurisdictions.useQuery and teamModeOrg is probably the result
  of useSelector(getOrgForTeamMode).

  If an org is currently selected, return only jurisdictions supported by that org. If
  No org is selected (personal workspace), return the super-set of all jurisdictions
  from all the user's org memberships
*/
// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
export const filterJurisdictionsByTeamMode = (
  supportedJurisdictions: Jurisdiction[] | undefined, // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  teamModeOrg: Organization | undefined,
): Jurisdiction[] => {
  let teamJurisdictions = supportedJurisdictions ?? [];
  const filterJurisdictions = teamModeOrg?.jurisdictions?.map((j) => j.abbreviation);
  if (filterJurisdictions && filterJurisdictions.length > 0) {
    teamJurisdictions = teamJurisdictions?.filter((j) =>
      filterJurisdictions.includes(j.abbreviation),
    );
  }
  return teamJurisdictions;
};

export const getLegislaturesOptions = (
  orgJurisdictions: Jurisdiction[],
  supportedJurisdictions?: Jurisdiction[],
): MultiSelectOption<string>[] => {
  let availableLegislatures = [
    {
      label: 'US Congress',
      value: 'usa',
    },
  ];

  if (orgJurisdictions.length > 0) {
    availableLegislatures = orgJurisdictions.map((orgJurisdiction) => ({
      label: orgJurisdiction.name,
      value: orgJurisdiction.abbreviation,
    }));
  }

  const legislaturesAccess = supportedJurisdictions
    ? getJurisdictionAccess(availableLegislatures, supportedJurisdictions)
    : availableLegislatures;

  return legislaturesAccess.sort((a, b) => a.label.localeCompare(b.label));
};

/* TODO: we need to add metadata to support operations on "All U.S. Jurisdictions"
 * so we don't have to keep updating this list with new jurisdictions from Africa or elsewhere
 */
export const filterUSJurisdictions = (jurisdictions: string[]): string[] => {
  const nonUsJurisdictions = ['aus', 'can', 'ng', 'za'];
  return jurisdictions.filter((j) => !nonUsJurisdictions.includes(j));
};

export const getDefaultJurisdictionFilter = (
  availableLegislatures: MultiSelectOption<string>[],
  organizationUser?: OrganizationUser,
): string[] => {
  const userJurisdictions = organizationUser && organizationUser.searchJurisdictions;
  const options =
    userJurisdictions && userJurisdictions.length > 0
      ? availableLegislatures.filter((legislature) => {
          return userJurisdictions
            .map((j) => j.abbreviation)
            .includes(legislature.value);
        })
      : [];
  return options.map((opt) => opt.value);
};
